<template>
  <div>
    <!-- Botão da IA Assistente -->
    <div class="dropdown d-inline-block ai-chat-dropdown enhanced-ai-chat">
      <button
        type="button"
        class="enhanced-ai-btn"
        :class="{ loading: loading }"
        @click="execute"
        :disabled="loading"
      >
        <img
          :src="require('../../assets/sistemaclinai.webp')"
          class="sistemaclin-ai mr-2"
          alt="SistemaClin AI"
        />
        <span>Análise de Sinais Vitais</span>
      </button>
    </div>

    <!-- Modal de Análise da IA -->
    <div
      v-if="showAnalysis"
      class="ai-analysis-modal"
      @click.self="closeAnalysis"
    >
      <div class="ai-analysis-content">
        <!-- Header do Modal -->
        <div class="ai-analysis-header">
          <div class="header-info">
            <img
              :src="require('../../assets/sistemaclinai.webp')"
              class="ai-logo"
              alt="SistemaClin AI"
            />
            <div>
              <h3>Análise de Sinais Vitais - IA</h3>
              <p class="patient-info">
                {{ pacienteNome }} • {{ pacienteIdade }} anos
              </p>
            </div>
          </div>
          <button @click="closeAnalysis" class="close-btn">&times;</button>
        </div>

        <!-- Classificação Principal -->
        <div class="main-classification">
          <div
            class="classification-card"
            :class="getClassificationClass(classificacao)"
          >
            <div class="classification-icon">
              <i :class="getClassificationIcon(classificacao)"></i>
            </div>
            <div class="classification-info">
              <h2>{{ formatClassification(classificacao) }}</h2>
              <p>Confiança: {{ confianca }}%</p>
              <small>Pontuação de Risco: {{ pontuacaoRisco }}</small>
              <div v-if="completudeDados" class="data-completeness">
                <small
                  >Dados Disponíveis: {{ completudeDados.available }}/{{
                    completudeDados.total
                  }}
                  ({{ completudeDados.percentage }}%)</small
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Dados Ausentes -->
        <div v-if="temDadosAusentes" class="missing-data-section">
          <h4>
            <i class="fas fa-exclamation-circle"></i> Dados Não Registrados
          </h4>
          <div class="missing-data-list">
            <div
              v-for="(missing, index) in dadosAusentes"
              :key="index"
              class="missing-item"
            >
              <i class="fas fa-info-circle"></i>
              <span>{{ missing }}</span>
            </div>
          </div>
          <div class="missing-data-note">
            <small
              ><i class="fas fa-info-circle"></i>
              A análise pode ser mais precisa com a coleta desses dados
            </small>
          </div>
        </div>

        <!-- Achados Críticos -->
        <div v-if="temAchadosCriticos" class="critical-findings">
          <h4><i class="fas fa-exclamation-triangle"></i> Achados Críticos</h4>
          <div class="findings-list">
            <div
              v-for="(finding, index) in achadosCriticos"
              :key="index"
              class="finding-item critical"
            >
              <i class="fas fa-circle"></i>
              <span>{{ finding }}</span>
            </div>
          </div>
        </div>

        <!-- Alertas Clínicos -->
        <div v-if="temAlertasClinicos" class="clinical-alerts">
          <h4><i class="fas fa-bell"></i> Alertas Clínicos</h4>
          <div class="findings-list">
            <div
              v-for="(alert, index) in alertasClinicos"
              :key="index"
              class="finding-item warning"
            >
              <i class="fas fa-circle"></i>
              <span>{{ alert }}</span>
            </div>
          </div>
        </div>

        <!-- Recomendações -->
        <div v-if="temRecomendacoes" class="recommendations">
          <h4><i class="fas fa-lightbulb"></i> Recomendações</h4>
          <div class="recommendations-list">
            <div
              v-for="(rec, index) in recomendacoes"
              :key="index"
              class="recommendation-item"
              :class="getPriorityClass(rec.prioridade)"
            >
              <div class="rec-header">
                <span class="rec-type">{{
                  formatRecommendationType(rec.tipo)
                }}</span>
                <span
                  class="rec-priority"
                  :class="getPriorityClass(rec.prioridade)"
                >
                  {{ formatPriority(rec.prioridade) }}
                </span>
              </div>
              <p class="rec-action">{{ rec.acao }}</p>
              <div
                v-if="rec.tempo_limite || rec.frequencia"
                class="rec-details"
              >
                <small v-if="rec.tempo_limite"
                  >Tempo limite: {{ rec.tempo_limite }}</small
                >
                <small v-if="rec.frequencia"
                  >Frequência: {{ rec.frequencia }}</small
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Sinais Vitais -->
        <div class="vital-signs-summary">
          <h4><i class="fas fa-heartbeat"></i> Sinais Vitais</h4>

          <!-- Sinais Vitais Disponíveis -->
          <div v-if="temSinaisVitaisDisponiveis" class="vitals-available">
            <h5>Dados Coletados</h5>
            <div class="vitals-grid">
              <div class="vital-item" v-if="temperatura">
                <span class="vital-label">Temperatura</span>
                <span
                  class="vital-value"
                  :class="getVitalStatus('temperatura', temperatura)"
                >
                  {{ temperatura }}°C
                </span>
              </div>
              <div class="vital-item" v-if="pressaoSistolica">
                <span class="vital-label">Pressão Arterial</span>
                <span
                  class="vital-value"
                  :class="getVitalStatus('pressao', pressaoSistolica)"
                >
                  {{ pressaoSistolica }}/{{ pressaoDiastolica }}
                </span>
              </div>
              <div class="vital-item" v-if="pulso">
                <span class="vital-label">Frequência Cardíaca</span>
                <span
                  class="vital-value"
                  :class="getVitalStatus('pulso', pulso)"
                >
                  {{ pulso }} bpm
                </span>
              </div>
              <div class="vital-item" v-if="saturacao">
                <span class="vital-label">Saturação O²</span>
                <span
                  class="vital-value"
                  :class="getVitalStatus('saturacao', saturacao)"
                >
                  {{ saturacao }}%
                </span>
              </div>
              <div class="vital-item" v-if="freqRespiratoria">
                <span class="vital-label">Freq. Respiratória</span>
                <span
                  class="vital-value"
                  :class="getVitalStatus('respiracao', freqRespiratoria)"
                >
                  {{ freqRespiratoria }} rpm
                </span>
              </div>
              <div class="vital-item" v-if="glicemia">
                <span class="vital-label">Glicemia</span>
                <span
                  class="vital-value"
                  :class="getVitalStatus('glicemia', glicemia)"
                >
                  {{ glicemia }} mg/dl
                </span>
              </div>
              <div class="vital-item" v-if="dor !== null">
                <span class="vital-label">Dor</span>
                <span class="vital-value" :class="getVitalStatus('dor', dor)">
                  {{ formatDor(dor) }}
                </span>
              </div>
              <div class="vital-item" v-if="peso">
                <span class="vital-label">Peso</span>
                <span class="vital-value vital-normal"> {{ peso }} kg </span>
              </div>
              <div class="vital-item" v-if="altura">
                <span class="vital-label">Altura</span>
                <span class="vital-value vital-normal"> {{ altura }} </span>
              </div>
            </div>
          </div>

          <!-- Sinais Vitais Ausentes -->
          <div v-if="temDadosFaltantes" class="vitals-missing">
            <h5>Dados Não Coletados</h5>
            <div class="missing-vitals-grid">
              <div
                v-for="(missing, index) in dadosFaltantes"
                :key="index"
                class="missing-vital-item"
              >
                <i class="fas fa-minus-circle"></i>
                <span>{{ missing }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Data da Análise -->
        <div class="analysis-footer">
          <small>
            <i class="fas fa-clock"></i>
            Análise realizada em {{ dataAnalise }}
          </small>
          <small>Método: {{ metodo }}</small>
        </div>
      </div>
    </div>

    <!-- Overlay -->
    <div v-if="showAnalysis" class="modal-overlay"></div>
  </div>
</template>

<script>
import { userData } from "../../lib/functions";
import api from "../../services/api";
import moment from "moment";

export default {
  name: "AIVitalSigns",

  components: {},

  data() {
    return {
      data: [],
      loading: false,
      showAnalysis: false,
      analysisData: {},
      photo_url: null,
    };
  },

  props: {
    resident_id: [String, Number],
  },

  computed: {
    // Dados do paciente
    pacienteNome() {
      if (this.analysisData.paciente && this.analysisData.paciente.nome) {
        return this.analysisData.paciente.nome;
      }
      return "Paciente";
    },

    pacienteIdade() {
      if (this.analysisData.paciente && this.analysisData.paciente.idade) {
        return this.analysisData.paciente.idade;
      }
      return "";
    },

    // Dados da análise
    classificacao() {
      if (
        this.analysisData.analise &&
        this.analysisData.analise.classificacao
      ) {
        return this.analysisData.analise.classificacao;
      }
      return "normal";
    },

    confianca() {
      if (this.analysisData.analise && this.analysisData.analise.confianca) {
        return this.analysisData.analise.confianca;
      }
      return "0.000";
    },

    pontuacaoRisco() {
      if (
        this.analysisData.analise &&
        this.analysisData.analise.pontuacao_risco
      ) {
        return this.analysisData.analise.pontuacao_risco;
      }
      return 0;
    },

    metodo() {
      if (this.analysisData.analise && this.analysisData.analise.metodo) {
        return this.analysisData.analise.metodo;
      }
      return "Regras Médicas";
    },

    // Completude dos dados
    completudeDados() {
      if (
        this.analysisData.analise &&
        this.analysisData.analise.completude_dados
      ) {
        return this.analysisData.analise.completude_dados;
      }
      return null;
    },

    // Dados ausentes
    dadosAusentes() {
      if (
        this.analysisData.analise &&
        this.analysisData.analise.dados_ausentes
      ) {
        return this.analysisData.analise.dados_ausentes;
      }
      return [];
    },

    temDadosAusentes() {
      return this.dadosAusentes.length > 0;
    },

    // Dados faltantes dos sinais vitais
    dadosFaltantes() {
      if (
        this.analysisData.sinais_vitais &&
        this.analysisData.sinais_vitais.dados_faltantes
      ) {
        return this.analysisData.sinais_vitais.dados_faltantes;
      }
      return [];
    },

    temDadosFaltantes() {
      return this.dadosFaltantes.length > 0;
    },

    // Achados críticos
    achadosCriticos() {
      if (
        this.analysisData.analise &&
        this.analysisData.analise.achados_criticos
      ) {
        return this.analysisData.analise.achados_criticos;
      }
      return [];
    },

    temAchadosCriticos() {
      return this.achadosCriticos.length > 0;
    },

    // Alertas clínicos
    alertasClinicos() {
      if (
        this.analysisData.analise &&
        this.analysisData.analise.alertas_clinicos
      ) {
        return this.analysisData.analise.alertas_clinicos;
      }
      return [];
    },

    temAlertasClinicos() {
      return this.alertasClinicos.length > 0;
    },

    // Recomendações
    recomendacoes() {
      if (this.analysisData.recomendacoes) {
        return this.analysisData.recomendacoes;
      }
      return [];
    },

    temRecomendacoes() {
      return this.recomendacoes.length > 0;
    },

    // Sinais vitais
    sinaisVitais() {
      if (
        this.analysisData.sinais_vitais &&
        this.analysisData.sinais_vitais.processados
      ) {
        return this.analysisData.sinais_vitais.processados;
      }
      return {};
    },

    temSinaisVitaisDisponiveis() {
      const vitals = this.sinaisVitais;
      return Object.values(vitals).some((value) => value !== null);
    },

    temperatura() {
      return this.sinaisVitais.temperatura;
    },

    pressaoSistolica() {
      return this.sinaisVitais.pressao_sistolica;
    },

    pressaoDiastolica() {
      return this.sinaisVitais.pressao_diastolica;
    },

    pulso() {
      return this.sinaisVitais.pulso;
    },

    saturacao() {
      return this.sinaisVitais.saturacao;
    },

    freqRespiratoria() {
      return this.sinaisVitais.freq_respiratoria;
    },

    glicemia() {
      return this.sinaisVitais.glicemia;
    },

    dor() {
      return this.sinaisVitais.dor;
    },

    peso() {
      return this.sinaisVitais.peso;
    },
    
    altura() {
      return this.sinaisVitais.altura;
    },

    // Data da análise
    dataAnalise() {
      if (
        this.analysisData.paciente &&
        this.analysisData.paciente.ultima_coleta
      ) {
        return moment(this.analysisData.paciente.ultima_coleta).format(
          "DD/MM/YYYY HH:mm"
        );
      }
      return moment().format("DD/MM/YYYY HH:mm");
    },
  },

  created() {
    let { photo_url } = userData().user;
    this.photo_url = photo_url;
  },

  methods: {
    moment,

    async execute() {
      try {
        this.loading = true;
        let { resident_id } = this;

        const responseAi = await api.get(
          `/ai/vital-signs/analyze/${resident_id}`
        );

        if (responseAi.data.success) {
          this.analysisData = responseAi.data;
          this.showAnalysis = true;
        } else {
          this.$toast.error(
            responseAi.data.message || "Erro ao analisar sinais vitais"
          );
        }
      } catch (error) {
        console.error("Erro na chamada da API:", error);
        this.$toast.error("Erro ao conectar com a IA. Tente novamente.");
      } finally {
        this.loading = false;
      }
    },

    closeAnalysis() {
      this.showAnalysis = false;
      this.analysisData = {};
    },

    getClassificationClass(classification) {
      if (classification === "crítico") return "critical";
      if (classification === "atenção") return "attention";
      if (classification === "indeterminado") return "indeterminate";
      return "normal";
    },

    getClassificationIcon(classification) {
      if (classification === "crítico") return "fas fa-exclamation-triangle";
      if (classification === "atenção") return "fas fa-exclamation-circle";
      if (classification === "indeterminado") return "fas fa-question-circle";
      return "fas fa-check-circle";
    },

    formatClassification(classification) {
      if (classification === "crítico") return "CRÍTICO";
      if (classification === "atenção") return "ATENÇÃO";
      if (classification === "indeterminado") return "INDETERMINADO";
      return "NORMAL";
    },

    getPriorityClass(priority) {
      if (priority === "crítica") return "priority-critical";
      if (priority === "alta") return "priority-high";
      if (priority === "média") return "priority-medium";
      return "priority-low";
    },

    formatPriority(priority) {
      if (priority === "crítica") return "CRÍTICA";
      if (priority === "alta") return "ALTA";
      if (priority === "média") return "MÉDIA";
      return "BAIXA";
    },

    formatRecommendationType(type) {
      const types = {
        emergencia: "Emergência",
        monitoramento: "Monitoramento",
        medicacao: "Medicação",
        investigacao: "Investigação",
        suporte: "Suporte",
        avaliacao: "Avaliação",
        suporte_respiratorio: "Suporte Respiratório",
        antipiretico: "Antitérmico",
        glicemia: "Controle Glicêmico",
        analgesia: "Analgesia",
        coleta_dados: "Coleta de Dados",
      };
      return types[type] || type;
    },

    formatDor(valorDor) {
      if (valorDor === 0) return "Sem dor";
      if (valorDor <= 3) return `Leve (${valorDor})`;
      if (valorDor <= 7) return `Moderada (${valorDor})`;
      return `Intensa (${valorDor})`;
    },

    getVitalStatus(type, value) {
      // Determina se o valor está normal, alterado ou crítico
      if (type === "temperatura") {
        if (value > 38.5 || value < 35.5) return "vital-critical";
        if (value > 37.5 || value < 36.0) return "vital-warning";
        return "vital-normal";
      }

      if (type === "pressao") {
        if (value > 180 || value < 90) return "vital-critical";
        if (value > 140 || value < 100) return "vital-warning";
        return "vital-normal";
      }

      if (type === "pulso") {
        if (value > 120 || value < 50) return "vital-critical";
        if (value > 100 || value < 60) return "vital-warning";
        return "vital-normal";
      }

      if (type === "saturacao") {
        if (value < 90) return "vital-critical";
        if (value < 95) return "vital-warning";
        return "vital-normal";
      }

      if (type === "respiracao") {
        if (value > 30 || value < 10) return "vital-critical";
        if (value > 24 || value < 12) return "vital-warning";
        return "vital-normal";
      }

      if (type === "glicemia") {
        if (value > 300 || value < 60) return "vital-critical";
        if (value > 180 || value < 70) return "vital-warning";
        return "vital-normal";
      }

      if (type === "dor") {
        if (value >= 8) return "vital-critical";
        if (value >= 5) return "vital-warning";
        return "vital-normal";
      }

      return "vital-normal";
    },
  },
};
</script>

<style scoped>
/* Estilos existentes do botão mantidos */
.enhanced-ai-chat {
  position: relative !important;
}

.enhanced-ai-btn {
  font-size: 12px;
  border: 1px solid #1f7fd3;
  border-radius: 20px !important;
  background: #1f7fd3 !important;
  background-size: 300% 300% !important;
  text-decoration: none !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  position: relative !important;
  overflow: hidden !important;
  /* animation: gradientShift 3s ease infinite, aiPulse 2s ease-in-out infinite !important; */
  animation: gradientShift 3s ease infinite !important;
  color: #fff;
  padding: 2px 10px;
  letter-spacing: 0.5px;
}

.enhanced-ai-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Animações existentes mantidas */
@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/*
@keyframes aiPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}
*/

.enhanced-ai-btn:hover {
  animation-play-state: paused !important;
  transform: translateY(-1px);
  color: white !important;
  text-decoration: none !important;
}

.sistemaclin-ai {
  width: 20px !important;
  height: 20px !important;
  object-fit: contain !important;
  animation: aiRotate 4s ease-in-out infinite !important;
}

@keyframes aiRotate {
  0% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(5deg);
  }
  50% {
    transform: rotate(0deg);
  }
  75% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

.enhanced-ai-btn:hover .sistemaclin-ai {
  animation: aiRotateFast 1s linear infinite !important;
}

@keyframes aiRotateFast {
  0% {
    transform: rotate(0deg) scale(1.1);
  }
  100% {
    transform: rotate(360deg) scale(1.1);
  }
}

/* Estilos do Modal de Análise */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.ai-analysis-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.ai-analysis-content {
  background: white;
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.ai-analysis-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #1f7fd3, #1e42af);
  color: white;
  border-radius: 16px 0 0 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ai-logo {
  width: 40px;
  height: 40px;
  background: transparent;
  border-radius: 50%;
}

.ai-analysis-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.patient-info {
  margin: 4px 0 0 0;
  opacity: 0.9;
  font-size: 0.9rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Classificação Principal */
.main-classification {
  padding: 24px;
}

.classification-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 8px;
}

.classification-card.critical {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
}

.classification-card.attention {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: white;
}

.classification-card.indeterminate {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white;
}

.classification-card.normal {
  background: linear-gradient(135deg, #059669, #047857);
  color: white;
}

.classification-icon {
  font-size: 2.5rem;
}

.classification-info h2 {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
}

.classification-info p {
  margin: 8px 0 4px 0;
  font-size: 1.1rem;
  opacity: 0.9;
}

.classification-info small {
  opacity: 0.8;
}

.data-completeness {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

/* Seção de Dados Ausentes */
.missing-data-section {
  padding: 0 24px 20px;
  background: #f8fafc;
  margin: 0 24px;
  border-radius: 8px;
  border-left: 4px solid #6b7280;
}

.missing-data-section h4 {
  color: #6b7280;
  margin: 0 0 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
  padding-top: 16px;
}

.missing-data-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.missing-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.missing-item i {
  color: #6b7280;
  font-size: 0.9rem;
}

.missing-data-note {
  padding: 8px 12px;
  background: #e0f2fe;
  border-radius: 6px;
  border-left: 3px solid #0891b2;
}

.missing-data-note small {
  color: #0c4a6e;
  font-weight: 500;
}

/* Achados e Alertas */
.critical-findings,
.clinical-alerts {
  padding: 0 24px 20px;
}

.critical-findings h4,
.clinical-alerts h4 {
  color: #dc2626;
  margin: 0 0 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.clinical-alerts h4 {
  color: #d97706;
}

.findings-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.finding-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
}

.finding-item.critical {
  background: #fef2f2;
  border-left: 4px solid #dc2626;
}

.finding-item.warning {
  background: #fefbf2;
  border-left: 4px solid #d97706;
}

.finding-item i {
  color: inherit;
  margin-top: 2px;
  font-size: 0.8rem;
}

.finding-item.critical i {
  color: #dc2626;
}

.finding-item.warning i {
  color: #d97706;
}

/* Recomendações */
.recommendations {
  padding: 0 24px 20px;
}

.recommendations h4 {
  color: #059669;
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.recommendation-item {
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #d1d5db;
}

.recommendation-item.priority-critical {
  background: #fef2f2;
  border-left-color: #dc2626;
}

.recommendation-item.priority-high {
  background: #fefbf2;
  border-left-color: #d97706;
}

.recommendation-item.priority-medium {
  background: #f0f9ff;
  border-left-color: #0ea5e9;
}

.rec-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.rec-type {
  font-weight: 600;
  color: #374151;
}

.rec-priority {
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
}

.rec-priority.priority-critical {
  background: #dc2626;
  color: white;
}

.rec-priority.priority-high {
  background: #d97706;
  color: white;
}

.rec-priority.priority-medium {
  background: #0ea5e9;
  color: white;
}

.rec-action {
  margin: 0 0 8px 0;
  color: #4b5563;
  line-height: 1.5;
}

.rec-details {
  display: flex;
  gap: 16px;
}

.rec-details small {
  color: #6b7280;
  font-size: 0.8rem;
}

/* Sinais Vitais */
.vital-signs-summary {
  padding: 0 24px 20px;
}

.vital-signs-summary h4 {
  color: #1f7fd3;
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.vitals-available h5,
.vitals-missing h5 {
  margin: 0 0 12px 0;
  font-size: 1rem;
  color: #374151;
}

.vitals-missing h5 {
  color: #6b7280;
}

.vitals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.vital-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.vital-label {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
}

.vital-value {
  font-weight: 600;
  font-size: 1rem;
}

.vital-value.vital-normal {
  color: #059669;
}

.vital-value.vital-warning {
  color: #d97706;
}

.vital-value.vital-critical {
  color: #dc2626;
}

/* Sinais Vitais Ausentes */
.missing-vitals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}

.missing-vital-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  border: 1px dashed #d1d5db;
}

.missing-vital-item i {
  color: #9ca3af;
  font-size: 0.9rem;
}

.missing-vital-item span {
  color: #6b7280;
  font-size: 0.9rem;
}

/* Footer */
.analysis-footer {
  padding: 20px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.analysis-footer small {
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Responsividade */
@media (max-width: 768px) {
  .ai-analysis-modal {
    padding: 10px;
  }

  .ai-analysis-content {
    max-height: 95vh;
    max-width: 100%;
  }

  .vitals-grid,
  .missing-vitals-grid {
    grid-template-columns: 1fr;
  }

  .classification-card {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .analysis-footer {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .rec-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .missing-data-section {
    margin: 0 16px;
  }
}

@media (max-width: 576px) {
  .sistemaclin-ai {
    width: 20px !important;
    height: 20px !important;
  }

  .ai-analysis-header {
    padding: 16px;
  }

  .main-classification,
  .critical-findings,
  .clinical-alerts,
  .recommendations,
  .vital-signs-summary {
    padding-left: 16px;
    padding-right: 16px;
  }

  .missing-data-section {
    margin: 0 8px;
  }
}
</style>